import { useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MentionsPage } from "@/features/mentions/MentionsPage";
import {
  emptySharedFilters,
  useMentionFilterStore,
} from "@/features/mentions/mention-filter-context";
import { stateFromCriteria } from "@/features/mentions/mention-filter-criteria";
import {
  MENTION_FILTER_PARAM,
  criteriaFromSearch,
} from "@/features/mentions/mention-filter-link";
import { ONLY_UPLOAD_PARAM } from "@/features/mentions/upload-mentions-link";
import { recorteToSearch, withAutoGenerate } from "@/features/reports/report-recorte";
import { useAppShell } from "@/shell/app-shell-context";
import { useRequireCompany } from "@/shared/hooks/useRequireCompany";
import { canOperate, hasRoleAtLeast } from "@/shared/utils/roles";

export function MentionsRoute() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, openMention, mentionChange, mentionChangeId, profile } = useAppShell();
  const company = useRequireCompany();

  // O filtro de upload mora na URL (`?upload=7&upload=4`), não em
  // estado local: é assim que a página de detalhe de upload linka pra
  // cá, e é o que deixa o recorte compartilhável/recarregável.
  // `getAll` porque é multisseleção — o parâmetro repete.
  //
  // **Memoizado pelo TEXTO dos parâmetros, de propósito** (2026-10-05).
  // A lista é dependência dos efeitos da tela que voltam pra página 1,
  // limpam a seleção e recarregam; e esta rota re-renderiza a cada
  // mudança do contexto da casca — o contador da sidebar (polling de
  // 15 s) e o `mentionChange` de cada edição no drawer. Um array novo
  // por render fazia salvar uma correção levar a pessoa de volta pra
  // página 1 com a seleção apagada, e a cada 15 s durante um upload.
  const uploadKey = searchParams.getAll("upload").join(",");
  const uploadFilter = useMemo(
    () =>
      uploadKey
        .split(",")
        .map(Number)
        .filter(value => Number.isInteger(value) && value > 0),
    [uploadKey]
  );

  // Os links do sino (`?reprocessamento=<id>`, `?previa=<id>`): a tela
  // abre a execução e a rota tira o parâmetro, pra um F5 não reabrir.
  const reprocessLink =
    Number(searchParams.get("reprocessamento")) || Number(searchParams.get("previa")) || null;
  const consumeReprocessLink = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("reprocessamento");
    next.delete("previa");
    setSearchParams(next, { replace: true });
  };

  // "Ver menções" de um upload (2026-10-26): `?upload=N&so_upload=1` pede
  // SÓ o upload. O filtro guardado da empresa é zerado e o sinal sai da
  // URL, ficando o `?upload=` — e a tela de menções só monta no render
  // seguinte, já com a loja limpa, então a primeira consulta sai apenas
  // com o upload (sem o período e os subtemas que a pessoa tinha). Um F5
  // ou um link compartilhado só com `?upload=` NÃO leva o sinal e não
  // apaga o filtro de ninguém. Effect comum, e não layout effect: o
  // `navigate` do react-router ignora chamada feita num layout effect
  // (medido: a URL nunca mudava, e a tela ficava em branco). Fica ANTES
  // do retorno por falta de empresa, pela ordem dos hooks.
  const store = useMentionFilterStore();
  const onlyUpload = searchParams.has(ONLY_UPLOAD_PARAM);
  useEffect(() => {
    if (!onlyUpload) return;
    store.setState(() => emptySharedFilters());
    const next = new URLSearchParams(searchParams);
    next.delete(ONLY_UPLOAD_PARAM);
    setSearchParams(next, { replace: true });
    // `store.setState` e `searchParams` mudam a cada render; o que
    // dispara a limpeza é só a presença do sinal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyUpload]);

  // O link já filtrado (`?filtro=`, 2026-11-02): mesmo desenho do
  // `so_upload` acima — o recorte do link SUBSTITUI o filtro guardado da
  // empresa, o parâmetro sai da URL, e a tela só monta no render seguinte,
  // com a loja já trocada. Link inválido não mexe em nada: a tela abre
  // com o filtro que a pessoa tinha.
  const filterLink = searchParams.has(MENTION_FILTER_PARAM);
  useEffect(() => {
    if (!filterLink) return;
    const criteria = criteriaFromSearch(searchParams);
    if (criteria) {
      const restored = stateFromCriteria(criteria, { showsCena: true });
      store.setState(() => ({
        ...emptySharedFilters(),
        filters: restored.filters,
        query: restored.query,
        savedIds: restored.savedIds ?? {},
      }));
    }
    const next = new URLSearchParams(searchParams);
    next.delete(MENTION_FILTER_PARAM);
    setSearchParams(next, { replace: true });
    // Mesmo motivo do efeito acima: dispara pela presença do sinal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterLink]);

  if (!company) return null;
  if (onlyUpload || filterLink) return null;

  // A tela busca os próprios dados no contrato real — com filtro e
  // paginação no servidor. O escopo por empresa viaja na credencial,
  // nunca em prop (mesmo padrão da tela de Uploads).
  return (
    <MentionsPage
      token={token}
      companyLogoUrl={company.logo_url}
      openMention={openMention}
      mentionChange={mentionChange}
      mentionChangeId={mentionChangeId}
      canReprocess={hasRoleAtLeast(profile, "administrator")}
      readOnly={!canOperate(profile)}
      reprocessLink={reprocessLink}
      onConsumeReprocessLink={consumeReprocessLink}
      onOpenReprocessRun={runId =>
        navigate(`/empresas/${company.id}/reprocessamentos/${runId}`)
      }
      uploadFilter={uploadFilter}
      onChangeUploadFilter={next =>
        setSearchParams(
          next.map<[string, string]>(id => ["upload", String(id)]),
          { replace: true }
        )
      }
      // Quem navega é a rota: ela tem o `navigate` e a empresa da URL.
      // A tela de menções só sabe DIZER qual é o recorte — assim ela
      // continua testável e ignorante de rota, como já era com o
      // filtro de upload.
      // `gerar=1`: o botão diz "Fechar amostra", então a página de
      // relatório fecha a amostra ao chegar, sem um segundo clique.
      onGenerateReport={recorte =>
        navigate({
          pathname: `/empresas/${company.id}/relatorios/cena`,
          search: withAutoGenerate(recorteToSearch(recorte))
        })
      }
      onOpenDashboardDraft={draftId =>
        navigate(`/empresas/${company.id}/dashboard?draft=${draftId}`)
      }
    />
  );
}
